"use client";

import { useEffect, useMemo } from "react";
import { useAtom } from "jotai";
import { DocumentCategory, DocumentTypes } from "@/prisma-package/src";
import {
  BUILT_IN_DOCUMENT_TYPES,
  mergeWithBuiltIns,
} from "@/prisma-package/src/utils/document-type-builtins";
import { DocumentTypeDefinitionRecord } from "@/prisma-package/src/entities/document-type-definition.entity";
import { documentTypeDefinitionsAtom } from "@/app/_lib/atoms/document-type-definitions.atom";
import { getDocumentTypeDefinitionsAction } from "@/app/_lib/actions/document-type.actions";
import { useOrgOptional } from "@/app/_lib/contexts/org-context";

const builtInFallback = (): DocumentTypeDefinitionRecord[] =>
  mergeWithBuiltIns([]).map((definition) => ({
    id: null,
    organizationId: null,
    projectIds: [],
    ...definition,
  }));

/**
 * Active document type definitions for client components: global types, the
 * current org's own, and — when a projectId is passed — that project's own.
 * Fetched once per scope and shared via a jotai atom; falls back to the
 * compiled built-ins while loading or if the fetch fails, so type filters
 * and labels always render.
 *
 * Pass projectId explicitly from components that have it (a prop or
 * useProjectId on a project route) — never derived here, since route [id]
 * segments are not always project ids. A caller WITHOUT a projectId accepts
 * whatever cache exists for the org: the with-project list is a superset, so
 * labels resolve for every key the caller can encounter. (Project-scoped
 * VENDOR_COMPLIANCE types can ride along in vendorComplianceTypes from such
 * a cache — harmless, since their keys only appear on that project's data.)
 */
export const useDocumentTypeDefinitions = (projectId?: string) => {
  const [cached, setCached] = useAtom(documentTypeDefinitionsAtom);
  const org = useOrgOptional();
  const orgId = org?.orgId ?? null;
  const wantedProjectId = projectId ?? null;

  useEffect(() => {
    const orgMatches = cached !== null && cached.orgId === orgId;
    const projectMatches =
      wantedProjectId === null || cached?.projectId === wantedProjectId;
    if (orgMatches && projectMatches) return;
    let cancelled = false;

    getDocumentTypeDefinitionsAction({
      ...(orgId && { organizationId: orgId }),
      ...(wantedProjectId && { projectId: wantedProjectId }),
    })
      .then(([error, result]) => {
        if (cancelled) return;
        if (error || !result) {
          console.error("Error loading document type definitions:", error);
          setCached({
            orgId,
            projectId: wantedProjectId,
            definitions: builtInFallback(),
          });
        } else {
          setCached({
            orgId,
            projectId: wantedProjectId,
            definitions: result,
          });
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error loading document type definitions:", error);
        setCached({
          orgId,
          projectId: wantedProjectId,
          definitions: builtInFallback(),
        });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cached, orgId, wantedProjectId]);

  const isLoaded =
    cached !== null &&
    cached.orgId === orgId &&
    (wantedProjectId === null || cached.projectId === wantedProjectId);
  const resolved = isLoaded ? cached.definitions : builtInFallback();

  const labelByKey = useMemo(() => {
    const map: Record<string, string> = {};
    for (const definition of resolved) {
      map[definition.key] = definition.label;
    }
    return map;
  }, [resolved]);

  const vendorComplianceTypes = useMemo(
    () =>
      resolved.filter(
        (definition) =>
          definition.category === DocumentCategory.VENDOR_COMPLIANCE &&
          definition.key !== DocumentTypes.OTHER
      ),
    [resolved]
  );

  const labelFor = (key: string): string =>
    labelByKey[key] ?? key.replace(/_/g, " ");

  return {
    definitions: resolved,
    isLoaded,
    vendorComplianceTypes,
    labelFor,
  };
};

export { BUILT_IN_DOCUMENT_TYPES };
